import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Alert,
  backLinkClasses,
  Badge,
  Button,
  Card,
  Field,
  Icon,
  Page,
  PageHeader,
  Select,
  Table,
  type Action,
} from '@evogard/ui';
import type { Category } from '@evogard/contract';
import { loadFailureMessage } from '@evogard/domain';
import { api, ok, errorMessage } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';

/**
 * Categoria e prazo, ajustáveis sem deploy.
 *
 * ## Por que esta tela nasce com a lista vazia
 *
 * A parte 7 decidiu que **a regra vem do aplicativo original** e que o que não
 * dá para ler fica de fora, inteiro. Semear "Financeiro / 5 dias" aqui seria um
 * número inventado que a tela apresentaria como fato — e um job diário cobrando
 * atraso em cima dele.
 *
 * O código do `ticketgard` foi lido em 26/08 (`demandas-01`), e ele **não tem a
 * lista**: as categorias foram digitadas pela operação direto em produção. Sai
 * de lá por `tools/extract-ticketgard.sql`, em `demandas-31`, e precisa
 * acontecer antes de o sistema antigo ser desligado — depois não há de onde
 * tirar.
 *
 * Então a lista nasce vazia e a tela diz exatamente isso: não é uma falha de
 * carregamento, é o que a casa sabe.
 *
 * ## Arquivar, nunca apagar
 *
 * Convenção 9. Categoria arquivada some da abertura de demanda nova e continua
 * legível na demanda antiga — apagar deixaria dois anos de histórico com um
 * buraco no lugar do que a pessoa escolheu.
 *
 * ## O desenho de 17/09 chegou aqui em 18/09, e ele é o da mesa de demandas
 *
 * Esta tela tinha três desvios do padrão, e os três eram invisíveis abrindo o
 * arquivo sozinho — só apareciam ao ler as duas telas lado a lado, que é a
 * forma de defeito que o `DESIGN.md` chama de divergência de conjunto:
 *
 * 1. **uma coluna "Ações" escrita à mão**, com dois botões por linha, onde a
 *    mesa já tinha as reticências e o painel (`rowActions`). Dois botões vezes
 *    trinta linhas é sessenta alvos competindo com o dado;
 * 2. **o formulário sempre aberto no alto da tela**, empurrando a lista para
 *    baixo — na mesa ele nasce fechado e é o botão do cabeçalho que o abre;
 * 3. **os campos em `flex-wrap` com largura escrita à mão** (`min-w-56`,
 *    `w-40`, `min-w-48`), que é o defeito medido em 06/09 na barra de
 *    filtros: largura fixa em contêiner elástico deixa um vão à direita que
 *    muda de tamanho a cada quebra. O formulário de abrir demanda empilha os
 *    campos em largura cheia, e agora este também.
 */
export function CategoriesScreen() {
  const client = useQueryClient();
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const canEdit = can(context, 'tickets.categories', 'editar');
  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  /*
   * O erro de arquivar mora na TELA, e não na linha.
   *
   * O `ActionsModal` fecha antes de a ação rodar — é construção dele, e o
   * docblock de lá diz por quê —, então não há onde mostrar a recusa dentro do
   * painel. Ela aparece aqui em cima, que é o mesmo lugar em que a mesa de
   * demandas mostra a recusa de aderir.
   */
  const [actionError, setActionError] = useState<string | null>(null);

  const categoriesQuery = useQuery({
    queryKey: ['demandas', 'categorias', tenantId, showArchived],
    queryFn: async () => {
      const r = await api.tickets.listCategories({
        // `'true'`/`'false'` e não booleano: a query string é texto, e agora o
        // contrato lê o texto em vez de coagi-lo — `'false'` finalmente quer
        // dizer falso. Ver o comentário do campo em `contract/src/demandas.ts`.
        query: { includeArchived: showArchived ? 'true' : 'false' },
      });
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'as categorias'));
      }
      return r.body;
    },
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId ? previous : undefined,
  });

  const archive = useMutation({
    mutationFn: async ({ cat, archived }: { cat: Category; archived: boolean }) => {
      const r = await api.tickets.editCategory({
        params: { id: cat.id },
        body: { archived },
      });
      if (r.status !== 200) {
        throw new Error(errorMessage(r.body, 'Não foi possível alterar a categoria.'));
      }
      return r.body;
    },
    onMutate: () => setActionError(null),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['demandas'] }),
    onError: (e: Error) => setActionError(e.message),
  });

  return (
    <Page measure="cheia">
      <PageHeader
        title="Categorias e prazos"
        back={
          <Link
            to="/tickets"
            activeOptions={{ exact: true }}
            className={backLinkClasses()}
          >
            <Icon nome="seta-esquerda" size={16} />
            Tickets
          </Link>
        }
        actions={
          /* Esconder é cortesia; quem protege é o guard. A rota continua
             exigindo `tickets.categories:editar`. */
          canEdit && (
            <Button icon="mais" onClick={() => setCreating(true)}>
              Nova categoria
            </Button>
          )
        }
      >
        Por base. O prazo é contado em dias corridos a partir da abertura.
      </PageHeader>

      {categoriesQuery.isError && (
        <Alert tone="erro">{categoriesQuery.error.message}</Alert>
      )}

      {actionError && <Alert tone="erro">{actionError}</Alert>}

      {/* O `Card` é da TELA, e não do formulário: no painel de reticências o
          mesmo componente ocupa o painel, e um cartão dentro do painel seria
          cartão dentro de cartão, que o `Card` proíbe no próprio docblock. */}
      {creating && canEdit && (
        <Card title="Nova categoria">
          <CategoryForm toClose={() => setCreating(false)} />
        </Card>
      )}

      {/* Erro sem dado nenhum: o aviso acima é a resposta inteira. A tabela
          anunciaria "Nenhuma categoria cadastrada" logo abaixo dele, e essa é a
          afirmação que parece um fato. */}
      {(!categoriesQuery.isError || categoriesQuery.data) && (
        <Card
          title="Categorias"
          actions={
            <label className="flex items-center gap-2 text-xs text-texto-suave">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="size-4 rounded border-borda-forte accent-primaria-texto"
              />
              Mostrar arquivadas
            </label>
          }
          bleed
        >
          <Table
            label="Categorias de ticket e seus prazos"
            items={categoriesQuery.data ?? []}
            loading={categoriesQuery.isLoading}
            rowKey={(cat) => String(cat.id)}
            empty={
              'Nenhuma categoria cadastrada nesta base. ' +
              'As categorias e os prazos vêm do sistema atual e são cadastrados aqui — ' +
              'a plataforma não inventa prazo.'
            }
            rowTitle={(cat) => cat.nome}
            rowSubtitle={(cat) =>
              cat.deadlineDays === null
                ? 'Sem prazo'
                : `Prazo de ${cat.deadlineDays === 1 ? '1 dia' : `${cat.deadlineDays} dias`}`
            }
            rowIcon="etiqueta"
            /*
             * Lista vazia para quem não pode editar, e não uma condicional em
             * volta: a tabela não desenha gatilho nenhum quando as ações somem,
             * em vez de abrir um painel vazio. O docblock de `rowActions` diz.
             */
            rowActions={(cat): Action[] => {
              if (!canEdit) return [];
              const archived = Boolean(cat.archivedAt);
              return [
                {
                  label: 'Renomear',
                  icon: 'etiqueta',
                  /*
                   * `form`, e não `run`: renomear pergunta o nome novo, e o
                   * painel troca de conteúdo no lugar. Antes disto era um
                   * `window.prompt` — sem rótulo, sem `maxLength` e sem estado
                   * de erro, devolvendo 400 do servidor para um nome curto.
                   */
                  form: (close) => <RenameForm categoria={cat} onDone={close} />,
                },
                {
                  /*
                   * "Configurar" e não "Editar": ao lado de "Renomear", que
                   * também edita, o rótulo genérico não diria qual dos dois faz
                   * o quê. Este abre prazo, setor que atende, quem pode abrir e
                   * portal — o mesmo formulário de criar, com os valores dela.
                   */
                  label: 'Configurar',
                  icon: 'pessoas',
                  form: (close) => <CategoryForm categoria={cat} toClose={close} />,
                },
                {
                  label: archived ? 'Reativar' : 'Arquivar',
                  icon: archived ? 'atualizar' : 'bloqueado',
                  /*
                   * `neutro` e não `perigo`: arquivar tem volta — a categoria
                   * some da abertura de demanda nova e continua legível na
                   * demanda antiga, e "Reativar" a traz de volta. `perigo` é
                   * para o que não se desfaz.
                   */
                  run: () => archive.mutate({ cat, archived: !archived }),
                },
              ];
            }}
            columns={[
              {
                title: 'Nome',
                alignment: 'inicio' as const,
                minWidth: '12rem',
                cell: (cat) => (
                  <span className={cat.archivedAt ? 'text-texto-suave' : undefined}>
                    {cat.nome}
                    {cat.archivedAt && (
                      <Badge tone="neutro" className="ml-2">
                        Arquivada
                      </Badge>
                    )}
                  </span>
                ),
              },
              {
                title: 'Prazo',
                cell: (cat) =>
                  cat.deadlineDays === null ? (
                    // "sem prazo" é resposta legítima, e a tela a diz
                    <span className="text-texto-suave">Sem prazo</span>
                  ) : (
                    <span className="tabular-nums">
                      {cat.deadlineDays === 1 ? '1 dia' : `${cat.deadlineDays} dias`}
                    </span>
                  ),
              },
              {
                title: 'Departamento',
                minWidth: '10rem',
                cell: (cat) =>
                  cat.departmentId === null ? (
                    /*
                     * "Todos que podem responder" e não "—".
                     *
                     * Categoria sem setor NÃO deixa a demanda em silêncio: o
                     * aviso cai para todo mundo que pode responder na base. Um
                     * travessão aqui pareceria uma configuração faltando com
                     * consequência desconhecida; a frase diz o que acontece.
                     */
                    <span className="text-texto-suave">Todos que podem responder</span>
                  ) : (
                    cat.departmentName
                  ),
              },
              {
                title: 'Quem pode abrir',
                minWidth: '9rem',
                cell: (cat) =>
                  !cat.restricted ? (
                    /* "Todos" e não "—", pela mesma razão da coluna ao lado: o
                       travessão pareceria configuração faltando, e este é o
                       estado normal de quase toda categoria. */
                    <span className="text-texto-suave">Todos</span>
                  ) : (
                    <Badge tone="informacao">
                      {cat.requesterDepartmentIds.length === 1
                        ? '1 setor'
                        : `${cat.requesterDepartmentIds.length} setores`}
                    </Badge>
                  ),
              },
              {
                title: 'Portal',
                cell: (cat) =>
                  cat.portalEnabled ? (
                    <span className="text-texto-suave">Sim</span>
                  ) : (
                    <Badge tone="neutro">Não</Badge>
                  ),
              },
              {
                title: 'Ordem',
                cell: (cat) => <span className="tabular-nums">{cat.order}</span>,
              },
            ]}
          />
        </Card>
      )}
    </Page>
  );
}

/**
 * Criar e configurar — o MESMO formulário, com os três campos que a categoria
 * declara além do nome.
 *
 * ## Os campos empilhados, como o de abrir demanda
 *
 * Eram três num `flex-wrap items-end` com largura escrita à mão em cada um
 * (`min-w-56 flex-1`, `w-40`, `min-w-48 flex-1`), e o botão "Criar" como quarto
 * item da mesma linha. O resultado é o que a medição de 06/09 achou na barra de
 * filtros: as larguras nunca fecham a linha, e o vão muda de tamanho a cada
 * quebra. Empilhado em largura cheia não há vão nenhum.
 *
 * ## Um componente para os dois atos, e sem `Card` por dentro
 *
 * Sem `categoria` é o modo criar, e quem o embrulha no `Card` é a tela; com
 * `categoria`, ele ocupa o painel de reticências da linha, como `RenameForm`. É
 * a mesma decisão de `DepartmentsForm` na tela de usuários: o formulário troca o
 * conteúdo do painel no lugar, em vez de empilhar um segundo diálogo.
 *
 * Duas formas para a mesma pergunta divergiriam no primeiro campo novo — e
 * "criar" e "configurar" perguntam exatamente as mesmas seis coisas.
 *
 * ## As três perguntas que a categoria responde
 *
 * O **departamento que atende** é para onde a demanda vai, e é obrigatório desde
 * 18/09. **Quem pode abrir** é a ponta oposta: de onde ela pode vir. E o
 * **portal** é um terceiro eixo, independente dos dois — o associado não tem
 * setor e nunca terá.
 */
function CategoryForm({
  categoria,
  toClose,
}: {
  /** ausente é criar; presente é configurar aquela categoria */
  categoria?: Category;
  toClose: () => void;
}) {
  const client = useQueryClient();
  const { data: context } = useSessionContext();
  const [nome, setName] = useState(categoria?.nome ?? '');
  const [deadline, setDeadline] = useState(
    categoria?.deadlineDays === null || categoria?.deadlineDays === undefined
      ? ''
      : String(categoria.deadlineDays),
  );
  const [departmentId, setDepartment] = useState(categoria?.departmentId ?? '');
  const [restricted, setRestricted] = useState(categoria?.restricted ?? false);
  const [chosen, setChosen] = useState<Set<string>>(
    () => new Set(categoria?.requesterDepartmentIds ?? []),
  );
  /*
   * Ao CRIAR nasce desmarcada, e o padrão do banco é o contrário.
   *
   * A coluna é `default true` para que a implantação não mude o que o associado
   * já vê; o formulário começa desmarcado para que expor uma categoria nova ao
   * portal seja uma decisão e não um esquecimento. Os dois padrões respondem a
   * perguntas diferentes — o passado e o futuro —, e é por isso que o contrato
   * não aceita o campo ausente: fosse opcional, um deles venceria em silêncio.
   */
  const [portalEnabled, setPortalEnabled] = useState(categoria?.portalEnabled ?? false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  /*
   * A lista de setores vem de `platform.departments:ver`, que é recurso de OUTRO
   * módulo — quem administra categoria não necessariamente o tem. Sem o
   * `enabled`, a consulta levaria 403 e o seletor renderizaria vazio, oferecendo
   * "sem departamento" como única escolha possível a quem só não pode ler a
   * lista. É a mesma armadilha já documentada na tela de usuários com grupos.
   */
  const canSeeDepartments = can(context, 'platform.departments', 'ver');
  const departments = useQuery({
    queryKey: ['plataforma', 'departamentos', context?.currentTenant.id, false],
    enabled: canSeeDepartments,
    queryFn: async () => {
      const r = await api.platform.listDepartments({ query: {} });
      return ok(r, 200, 'Não foi possível carregar os departamentos.');
    },
  });

  const save = useMutation({
    mutationFn: async () => {
      const common = {
        nome: nome.trim(),
        // vazio quer dizer "sem prazo", não zero: zero venceria no mesmo dia
        deadlineDays: deadline.trim() === '' ? null : Number(deadline),
        departmentId,
        restricted,
        /* só os marcados, e só quando restrita — o servidor descarta de todo
           jeito, mas mandar a lista de uma categoria aberta faria a requisição
           afirmar uma coisa que não vale */
        requesterDepartmentIds: restricted ? [...chosen] : [],
        portalEnabled,
      };

      if (categoria) {
        const r = await api.tickets.editCategory({
          params: { id: categoria.id },
          body: common,
        });
        if (r.status !== 200) {
          throw new Error(errorMessage(r.body, 'Não foi possível salvar a categoria.'));
        }
        return r.body;
      }

      const r = await api.tickets.createCategory({ body: common });
      return ok(r, 201, 'Não foi possível criar a categoria.');
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['demandas'] });
      toClose();
    },
    onMutate: () => setError(null),
    onError: (e: Error) => setError(e.message),
  });

  const available = departments.data ?? [];
  const visible = available.filter((d) =>
    d.nome.toLowerCase().includes(search.trim().toLowerCase()),
  );
  /** o setor escolhido para ATENDER, para o aviso de setor sem ninguém */
  const answering = available.find((d) => d.id === departmentId);

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        save.mutate();
      }}
    >
      {error && <Alert tone="erro">{error}</Alert>}

      {/*
       * A ORDEM que o setor obrigatório criou, dita em vez de descoberta.
       *
       * O docblock de `tickets_categories.department_id` recusava a estrangeira
       * obrigatória justamente por isto: as duas tabelas nascem vazias, e exigir
       * o setor "impediria cadastrar a primeira categoria antes do primeiro
       * setor — uma ordem que ninguém declarou e que a tela não teria como
       * explicar". A obrigatoriedade entrou por decisão do dono em 18/09/2026, e
       * o preço é este aviso: a ordem passa a ser declarada, com o caminho para
       * cumpri-la a um clique.
       *
       * Sem ele, uma base recém-instalada mostraria um seletor com uma opção só
       * ("Escolha um departamento") e um botão desabilitado sem motivo visível.
       */}
      {canSeeDepartments && !departments.isLoading && available.length === 0 && (
        <Alert tone="alerta">
          Esta base ainda não tem nenhum departamento, e toda categoria precisa de um que
          a atenda.{' '}
          <Link to="/departments" className="underline">
            Cadastre o primeiro departamento
          </Link>{' '}
          e volte aqui.
        </Alert>
      )}

      <Field
        label="Nome"
        required
        maxLength={80}
        value={nome}
        onChange={(e) => setName(e.target.value)}
        placeholder="Como a operação chama isto"
      />

      <Field
        label="Prazo (dias)"
        type="number"
        min={0}
        max={3650}
        value={deadline}
        onChange={(e) => setDeadline(e.target.value)}
        placeholder="—"
        help="Vazio = sem prazo"
      />

      {canSeeDepartments && (
        <Select
          label="Departamento que atende"
          required
          value={departmentId}
          disabled={departments.isLoading}
          onChange={(e) => setDepartment(e.target.value)}
          help="Quem recebe o aviso quando um ticket desta categoria chega"
        >
          <option value="">Escolha um departamento</option>
          {available.map((d) => (
            <option key={d.id} value={d.id}>
              {d.nome}
            </option>
          ))}
        </Select>
      )}

      {/*
       * Avisa em vez de RECUSAR, como a tela de vínculo faz com a marca de
       * liderança inerte. O setor obrigatório garante que a demanda tem um
       * destino declarado; ele NÃO garante que há alguém lá para responder, e
       * essas são duas coisas. Travar o salvar obrigaria a resolver o cadastro
       * de pessoas antes de cadastrar a categoria — uma ordem que ninguém
       * declarou.
       */}
      {answering?.memberCount === 0 && (
        <Alert tone="alerta">
          {answering.nome} não tem ninguém hoje. O ticket vai nascer nessa fila e o aviso
          não chega a pessoa nenhuma até alguém entrar no departamento.
        </Alert>
      )}

      {canSeeDepartments && (
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-medium">Quem pode abrir</legend>

          {/*
           * "Todos" é a AUSÊNCIA de restrição, e não todas as caixas marcadas. A
           * diferença aparece daqui a um mês: marcando as N caixas, o
           * departamento criado depois nasce de fora e some da categoria sem
           * ninguém ter decidido isso.
           */}
          <label className="flex min-h-11 items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!restricted}
              onChange={(e) => setRestricted(!e.target.checked)}
              className="size-4 rounded border-borda-forte accent-primaria-texto"
            />
            Todos os departamentos — inclusive os que forem criados depois
          </label>

          {restricted && (
            <div className="flex flex-col gap-2 rounded-lg border border-borda p-3">
              <Field
                label="Buscar departamento"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Parte do nome"
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="contorno"
                  /* marca os VISÍVEIS, não a base inteira: com a busca aberta,
                     "marcar todos" que ignorasse o filtro marcaria o que a
                     pessoa não está vendo */
                  onClick={() =>
                    setChosen((current) => {
                      const next = new Set(current);
                      for (const d of visible) next.add(d.id);
                      return next;
                    })
                  }
                >
                  Marcar {search.trim() === '' ? 'todos' : 'os encontrados'}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="contorno"
                  onClick={() =>
                    setChosen((current) => {
                      const next = new Set(current);
                      for (const d of visible) next.delete(d.id);
                      return next;
                    })
                  }
                >
                  Desmarcar {search.trim() === '' ? 'todos' : 'os encontrados'}
                </Button>
              </div>

              {visible.length === 0 ? (
                <p className="text-sm text-texto-suave">
                  {available.length === 0
                    ? 'Esta base ainda não tem departamento cadastrado.'
                    : 'Nenhum departamento com esse nome.'}
                </p>
              ) : (
                <div className="flex flex-col">
                  {visible.map((d) => (
                    <label
                      key={d.id}
                      className="flex min-h-11 items-center gap-2 text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={chosen.has(d.id)}
                        onChange={(e) =>
                          setChosen((current) => {
                            const next = new Set(current);
                            if (e.target.checked) next.add(d.id);
                            else next.delete(d.id);
                            return next;
                          })
                        }
                        className="size-4 rounded border-borda-forte accent-primaria-texto"
                      />
                      {d.nome}
                    </label>
                  ))}
                </div>
              )}

              {/* o estado que o servidor recusa, dito antes de a pessoa
                  esbarrar nele */}
              {chosen.size === 0 && (
                <p className="text-xs text-alerta">
                  Sem nenhum setor marcado, ninguém consegue abrir nesta categoria — e é
                  por isso que o sistema não deixa salvar assim. Marque ao menos um, ou
                  volte para “Todos os departamentos”.
                </p>
              )}
            </div>
          )}
        </fieldset>
      )}

      <label className="flex min-h-11 items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={portalEnabled}
          onChange={(e) => setPortalEnabled(e.target.checked)}
          className="size-4 rounded border-borda-forte accent-primaria-texto"
        />
        Permitir que o associado abra ticket nesta categoria, pelo portal
      </label>

      <div className="flex gap-2">
        <Button
          type="submit"
          loading={save.isPending}
          disabled={
            !nome.trim() || departmentId === '' || (restricted && chosen.size === 0)
          }
        >
          {categoria ? 'Salvar' : 'Criar'}
        </Button>
        <Button type="button" variant="contorno" onClick={toClose}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}

/**
 * Renomear, dentro do painel de ações.
 *
 * ## Duas coisas que esta edição fazia errado, e ficam registradas
 *
 * **Renomeava por `window.prompt`.** Sem rótulo, sem `maxLength`, sem estado de
 * erro e sem nenhum dos componentes da casa. O contrato exige de 2 a 80
 * caracteres; o `prompt` aceita qualquer coisa, e o servidor devolvia 400.
 *
 * **E a mutação não tinha `onError`.** Então aquele 400 — e o 403 de quem não
 * pode editar, e o 404 da categoria de outra base — não aparecia em lugar
 * nenhum: o botão parava de girar, a linha continuava igual, e a pessoa ficava
 * com "cliquei e não aconteceu nada".
 *
 * O conserto de 09/09 foi um formulário em linha, na própria célula. Ele saiu
 * em 18/09 pelo desenho de 17/09: a célula de ações virou reticências, e uma
 * linha de tabela que se transforma em formulário empurra as vizinhas para
 * baixo e muda a largura das colunas enquanto a pessoa digita. O painel é o
 * mesmo lugar, sem o salto — e continua sendo uma pergunta só, com o erro ao
 * lado do campo que o causou.
 */
function RenameForm({ categoria, onDone }: { categoria: Category; onDone: () => void }) {
  const client = useQueryClient();
  const [nome, setName] = useState(categoria.nome);
  const [error, setError] = useState<string | null>(null);

  const rename = useMutation({
    mutationFn: async (clean: string) => {
      const r = await api.tickets.editCategory({
        params: { id: categoria.id },
        body: { nome: clean },
      });
      if (r.status !== 200) {
        // a mensagem do servidor, não uma genérica: a recusa mais comum aqui é
        // o tamanho do nome, e é ela que diz qual é o limite
        throw new Error(errorMessage(r.body, 'Não foi possível alterar a categoria.'));
      }
      return r.body;
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['demandas'] });
      onDone();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        const clean = nome.trim();
        // nome igual não é alteração: gravar de novo gastaria uma requisição e
        // um evento de auditoria para dizer que nada mudou
        if (clean === categoria.nome) return onDone();
        rename.mutate(clean);
      }}
    >
      <Field
        label="Novo nome"
        required
        autoFocus
        minLength={2}
        maxLength={80}
        value={nome}
        onChange={(e) => setName(e.target.value)}
        error={error ?? undefined}
      />

      <div className="flex gap-2">
        <Button type="submit" loading={rename.isPending}>
          Salvar
        </Button>
        <Button type="button" variant="contorno" onClick={onDone}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}
